import { createContext, useContext, useMemo, useCallback } from 'react';
// eslint-disable-next-line max-len
import { type VariantConfiguratorWithPermissionsSpecsFragment } from '../../../api/fragments/VariantConfiguratorWithPermissionsSpecs';
import { useGetVariantConfiguratorQuery } from '../../../api/queries/getVariantConfigurator';
import LoadingElement from '../../../components/LoadingElement';
import NotFoundResult from '../../../components/results/NotFoundResult';

type VariantConfiguratorContextProps = {
    variantConfigurator: VariantConfiguratorWithPermissionsSpecsFragment;
    refetch: () => Promise<VariantConfiguratorWithPermissionsSpecsFragment>;
};

export const VariantConfiguratorContext = createContext<VariantConfiguratorContextProps | null>(null);

export const useVariantConfigurator = () => {
    const context = useContext(VariantConfiguratorContext);

    if (!context) {
        throw new Error('useVariantConfigurator must be used within a VariantConfiguratorProvider');
    }

    return context;
};

type VariantConfiguratorProviderProps = {
    variantConfiguratorId: string;
    children: React.ReactNode;
};

export const VariantConfiguratorProvider = ({ variantConfiguratorId, children }: VariantConfiguratorProviderProps) => {
    const { data, loading, refetch } = useGetVariantConfiguratorQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            id: variantConfiguratorId,
        },
    });

    const refetchVariantConfigurator = useCallback(async () => {
        const result = await refetch();

        return result?.data?.variantConfigurator;
    }, [refetch]);

    const context = useMemo(
        () => ({
            variantConfigurator: data?.variantConfigurator,
            refetch: refetchVariantConfigurator,
        }),
        [data?.variantConfigurator, refetchVariantConfigurator]
    );
    const variantConfigurator = data?.variantConfigurator;

    if (!variantConfigurator && loading) {
        return <LoadingElement />;
    }

    // TODO: permissions check here
    if (!variantConfigurator) {
        return <NotFoundResult />;
    }

    return <VariantConfiguratorContext.Provider value={context}>{children}</VariantConfiguratorContext.Provider>;
};
